import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import Axios from '@bobex/react-core/services/AxiosInterceptorsService';

export const useHttpErrorNotifications = () => {
    const { t } = useTranslation();

    useEffect(() => {
        const interceptorId = Axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.code === 'ERR_CANCELED') {
                    return Promise.reject(error);
                }

                const status: number | undefined = error?.response?.status;

                if (status === 401) {
                    // Handled by AxiosInterceptorsService (token refresh)
                    return Promise.reject(error);
                }

                if (error?.config?.suppressErrorToast) {
                    // The caller shows its own, more precise message
                    return Promise.reject(error);
                }

                const messageKey = status
                    ? `http.error.${status}`
                    : 'http.error.default';

                const message = t(messageKey, t('http.error.default'));

                const detail: string | undefined = status === 403
                    ? (error?.response?.data?.params?.error ?? error?.response?.data?.message ?? undefined)
                    : undefined;

                const endpoint: string | undefined = error?.config?.url ?? undefined;

                const authorities: string | undefined = status === 403
                    ? error?.response?.data?.params?.authority
                    : undefined;

                notifications.show({
                    title: t('http.error.title'),
                    message: (
                        <>
                            <span>{message}</span>
                            {detail && <><br /><span style={{ fontSize: '0.85em', opacity: 0.8 }}>{detail}</span></>}
                            {authorities && <><br /><span style={{ fontSize: '0.85em', opacity: 0.8 }}>Missing Authority: {authorities}</span></>}
                            {endpoint && <><br /><span style={{ fontSize: '0.75em', opacity: 0.5, fontFamily: 'monospace' }}>{endpoint}</span></>}
                        </>
                    ),
                    color: 'red',
                    autoClose: 6000,
                });

                return Promise.reject(error);
            }
        );

        return () => {
            Axios.interceptors.response.eject(interceptorId);
        };
    }, [t]);
};
